export function parentTitle(parent) {
  if (!parent) return "";
  const parentTitle = parent.querySelector(
    "bookmap > chapter > concept > ditatitle",
  );
  let title = "";
  if (parentTitle) {
    title = parentTitle.innerText || parentTitle.innerHTML;
  }
  title = title?.replace(/\s+/g, " ").trim();
  return title;
}

export function menuItemTitle(parentWithConcept) {
  let title = ditaTitle(parentWithConcept);

  if (!title) {
    title = conceptTitle(parentWithConcept);
  }

  if (!title) {
    title = topicTitle(parentWithConcept);
  }

  if (title) {
    title = title.innerText || title.innerHTML;
  }
  title = title?.replace(/\s+/g, " ").trim();
  return title;
}

function processChapter(chapter) {
  return {
    title: menuItemTitle(chapter),
    href: chapter.getAttribute("href"),
    msPath: chapter.getAttribute("mpspath"),
    level: chapter.getAttribute("level"),
    type: "chapter",
    className: chapter.getAttribute("class"),
    children: processChildren(Array.from(chapter.childNodes).slice(1)),
  };
}

export function processChildren(children) {
  return children.flatMap((child) => {
    const filteredNode = excludeNode(child);
    if (isTopicRef(filteredNode)) return processTopicRef(child);
    if (isMapRef(filteredNode)) return processMapRef(child);
    if (isChapterNode(filteredNode)) return processChapter(child);
    // To add the reference to the menu list when reading XML menus
    if (isTopicHead(filteredNode)) {
      const topicRef = Array.from(child.children).find(
        (i) => i.nodeName === "TOPICREF",
      );
      if (topicRef) {
        return processTopicRef(topicRef);
      }
    }
    return [];
  });
}

function processTopicRef(topicRef) {
  return {
    id: encodeURIComponent(topicRef.getAttribute("href")),
    title: menuItemTitle(topicRef),
    msPath: topicRef.getAttribute("mpspath"),
    type: "topicref",
    level: topicRef.getAttribute("level"),
    className: topicRef.getAttribute("class"),
    children: processChildren(Array.from(topicRef.childNodes)),
  };
}

function processLinks(topicRef) {
  return {
    id: encodeURIComponent(topicRef.getAttribute("href")),
    className: topicRef.getAttribute("class"),
  };
}

function processMapRef(navigationItem) {
  if (!navigationItem) return [];

  return Array.from(navigationItem.childNodes).flatMap((child) => {
    if (!child.querySelector) return [];

    const mapElement = child.querySelector(":scope > map");
    const topicrefElement = child.querySelector(":scope > topicref");
    const chapterElement = child.querySelector(":scope > chapter");

    if (mapElement) return processMapRef(mapElement);
    if (topicrefElement) return processTopicRef(topicrefElement);
    if (chapterElement) return processChapter(chapterElement);

    return [];
  });
}

function filterMenuItems(items) {
  if (!items || !Array.isArray(items)) return [];

  return items.map((item) => {
    // Create a copy of the item to avoid mutation issues
    const processedItem = { ...item };

    // Process children recursively first if they exist
    if (processedItem.children && Array.isArray(processedItem.children)) {
      // If level is greater than 2, remove all children
      if (processedItem.level && parseInt(processedItem.level, 10) > 2) {
        processedItem.children = [];
      } else {
        // Recursively process children
        processedItem.children = filterMenuItems(processedItem.children);

        // If not a chapter and has 0 or 1 children, empty the children array
        // To keep the logic the same, removing references from menu list
        const filteredChildren = processedItem.children.filter(
          (child) => child.title?.trim().toLowerCase() !== "references",
        );
        if (processedItem.type !== "chapter" && filteredChildren.length <= 1) {
          processedItem.children = [];
        }
      }
    }

    // Return the processed item
    return processedItem;
  });
}

export default function generateMenuFromXml(xmlString) {
  const chapter = getChapterRootFromXml(xmlString);
  const title = parentTitle(chapter);
  const chapterData = getMenuItems(chapter);
  const filteredChapterData = filterMenuItems(chapterData);
  const relatedLinks = getRelatedLinks(xmlString);
  return {
    title: title,
    menuItems: filteredChapterData,
    relatedLinks: relatedLinks,
  };
}
export function extractNewAnddeclaration(xmlString) {
  const sanitizedXml = xmlString?.replace(/\r?\n|\r/g, "");
  const xmlDoc = new DOMParser().parseFromString(sanitizedXml, "text/xml");

  const xref = xmlDoc.querySelector('section[outputclass="docversion"] xref');
  const whatsNewLink = xref ? xref.getAttribute("href") : null;
  const doiRef = xmlDoc.querySelector('related-links link[format="pdf"]');
  const declarationLink = doiRef ? doiRef.getAttribute("href") : null;

  return { whatsNewLink, declarationLink };
}

/**
 * Extracts the acknowledgement file path from XML content
 * @param {string} xmlString - The XML content to parse
 * @returns {string|null} The normalized path or null if not found
 */

export function getRelatedLinks(xmlString) {
  try {
    const sanitizedXml = xmlString.replace(/\r?\n|\r/g, "");
    const xmlDoc = new DOMParser().parseFromString(sanitizedXml, "text/html");

    const relatedLinks = xmlDoc.querySelectorAll("concept related-links link");
    const linkTextNodes = xmlDoc.querySelectorAll(
      "concept related-links linktext",
    );

    const links = Array.from(relatedLinks).map((link, index) => ({
      href: link.getAttribute("href"),
      format: link.getAttribute("format") || "html",
      text: linkTextNodes[index]?.innerText?.trim() || null,
    }));

    return links;
  } catch (error) {
    console.error("Failed to extract related-links:", error);
    return [];
  }
}

export function getChapterRootFromXml(xmlString) {
  // The XML is badly formatted, so this allows it to process.
  const sanitizedXml = xmlString.replace(/\r?\n|\r/g, "");
  const xmlDoc = new DOMParser().parseFromString(sanitizedXml, "text/html");

  return xmlDoc.querySelector("bookmap chapter");
}

export function getMenuItems(chapterDetails) {
  const result = Array.from(chapterDetails?.childNodes || []).flatMap(
    (item) => {
      if (
        item.getAttribute &&
        (item.getAttribute("mpspath") || item.getAttribute("href"))
      ) {
        const filteredNode = excludeNode(item);
        if (isTopicRef(filteredNode)) return processTopicRef(item);
        if (isMapRef(filteredNode)) return processMapRef(item);
        if (isChapterNode(filteredNode)) return processChapter(item);
      }
      return [];
    },
  );
  return result;
}

export function excludeNode(node) {
  if (
    node?.getAttribute &&
    node.getAttribute("processing-role") !== "resource-only"
  ) {
    return node;
  }
  return null;
}

function isChapterNode(node) {
  return isNodeTypeOf(node, "CHAPTER");
}

export function isMapRef(node) {
  return isNodeTypeOf(node, "MAPREF");
}

export function isTopicRef(node) {
  return isNodeTypeOf(node, "TOPICREF");
}

function isTopicHead(node) {
  return isNodeTypeOf(node, "TOPICHEAD");
}

function isNodeTypeOf(node, type) {
  return node && node.nodeName === type;
}

function ditaTitle(node) {
  if (!node) return null;
  return node.querySelector(":scope > ditatitle");
}

function conceptTitle(node) {
  if (!node) return null;
  return node.querySelector(":scope > concept > ditatitle");
}

function topicTitle(node) {
  if (!node) return null;
  return node.querySelector(":scope > topic > ditatitle");
}
